import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Archive, LayoutGrid, Loader2, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ActionButton } from '@components/Table';
import boardService from '@services/boardService';
import type { Board } from '@/types/board';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { colorClasses } from '@/utils/colors';
import { formatDateBR } from '@/utils/formatters';
import { BoardEditorModal } from './BoardEditorModal';

/** Quadros do usuário: dados e colunas (ordem, cor, WIP, concluído). */
export const BoardAdminView: React.FC = () => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editingId, setEditingId] = useState<number | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Board | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBoards(await boardService.getAll({ includeArchived }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await boardService.delete(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const status = (b: Board) =>
    b.is_archived ? (
      <span className="badge-neutral inline-flex items-center gap-1">
        <Archive size={11} /> Arquivado
      </span>
    ) : (
      <span className="badge-success">Ativo</span>
    );

  const period = (b: Board) =>
    b.start_date || b.end_date ? `${formatDateBR(b.start_date) || '…'} a ${formatDateBR(b.end_date) || '…'}` : null;

  const actions = (b: Board) => (
    <div className="flex items-center justify-end gap-1">
      <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditingId(b.id)} />
      <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(b)} />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <LayoutGrid size={20} className="text-slate-500" /> Quadros
        </h1>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Mostrar arquivados
          </label>
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setEditingId(null)}>
            Novo quadro
          </Button>
        </div>
      </div>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && boards.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && boards.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum quadro cadastrado.</p>}
        {boards.map((b) => (
          <div key={b.id} className={clsx('card p-3 space-y-2', b.is_archived && 'opacity-60')}>
            <div className="flex items-center gap-2">
              <span className={clsx('w-3 h-3 rounded-full flex-shrink-0', colorClasses(b.color).swatch)} />
              <Link to={`/kanban/${b.id}`} className="font-medium text-slate-800 hover:text-emerald-700 truncate">
                {b.name}
              </Link>
              {b.is_main && (
                <span title="Quadro principal" className="text-amber-500">
                  <Star size={14} fill="currentColor" />
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small">
              <span>{BOARD_TYPE_LABELS[b.board_type]}</span>
              {period(b) && <span>{period(b)}</span>}
              <span>
                {b.open_count} abertas{b.overdue_count > 0 ? ` · ${b.overdue_count} atrasadas` : ''}
              </span>
              {status(b)}
            </div>
            {actions(b)}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Quadro</th>
                <th>Tipo</th>
                <th>Período</th>
                <th>Abertas</th>
                <th>Situação</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && boards.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && boards.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-500 py-8">
                    Nenhum quadro cadastrado.
                  </td>
                </tr>
              )}
              {boards.map((b) => (
                <tr key={b.id} className={clsx('table-row', b.is_archived && 'opacity-60')}>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <span className={clsx('w-3 h-3 rounded-full flex-shrink-0', colorClasses(b.color).swatch)} />
                      <Link to={`/kanban/${b.id}`} className="font-medium text-slate-800 hover:text-emerald-700">
                        {b.name}
                      </Link>
                      {b.is_main && (
                        <span title="Quadro principal" className="text-amber-500">
                          <Star size={14} fill="currentColor" />
                        </span>
                      )}
                    </div>
                    {b.description && <p className="text-small mt-0.5 line-clamp-1">{b.description}</p>}
                  </td>
                  <td className="table-cell">{BOARD_TYPE_LABELS[b.board_type]}</td>
                  <td className="table-cell whitespace-nowrap">{period(b) ?? <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">
                    {b.open_count}
                    {b.overdue_count > 0 && <span className="text-red-600 text-xs ml-2">{b.overdue_count} atrasadas</span>}
                  </td>
                  <td className="table-cell">{status(b)}</td>
                  <td className="table-cell">{actions(b)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingId !== undefined && (
        <BoardEditorModal
          boardId={editingId}
          onClose={() => {
            setEditingId(undefined);
            load();
          }}
          onCreated={(id) => {
            setEditingId(id);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Excluir quadro"
        message={`Excluir o quadro "${deleting?.name ?? ''}"? Ele deixa de aparecer (exclusão lógica). O quadro principal não pode ser excluído — defina outro como principal antes.`}
        confirmText="Excluir"
        severity="danger"
        loading={busy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
